import moment, { Moment } from 'moment-timezone';
import { cloneDeep } from 'lodash-es';
import { DAY_END } from "external-services/date-utils-service/types";
import { DurationInputArg2, MomentInput, MomentInputObject } from 'moment';
import DayEndTime from 'external-services/date-utils-service/dayEndTime';

const NS = 'DateUtilsService';
const BOOKING_START_TIME_OFFSET = 30 // in mins


export default class DateUtilsService {
  /**
   * Gets time in minutes between 2 date times
   * @param earlierTime - often this refers to the first time in the first service of a schedule
   * @param laterTime - often this refers to the first time in a later service
   * @return difference between the two in minutes
   */
  static dateTimeNum(earlierTime: Date, laterTime: Date): number {
    const earlierTimeInMins = earlierTime.getTime() / 1000 / 60;
    const laterTimeInMins = laterTime.getTime() / 1000 / 60;
    return laterTimeInMins - earlierTimeInMins;
  }

  /**
   * Checks if a specified time is more than or equal to a start time and less than an end time
   * @param date - time to check
   * @param startDate - start time
   * @param endDate - end time
   * @return true if within, false otherwise
   */
  static isInBetweenDates(date: Date, startDate: Date, endDate: Date): boolean {
    return date < endDate && date >= startDate;
  }
  static isTimeSmallerThanDayEnd(date: Date): boolean {
    return DayEndTime.isTimeSmallerThanDayEnd(date);
  }
  static isTimeGreaterThanDayEnd(date: Date): boolean {
    return DayEndTime.isTimeGreaterThanDayEnd(date);
  }
  static isTimeGreaterOrEqualsDayEnd(date: Date): boolean {
    return DayEndTime.isTimeGreaterOrEqualsDayEnd(date);
  }
  static isTimeEqualsDayEnd(date: Date): boolean {
    return DayEndTime.isTimeEqualsDayEnd(date);
  }
  static isDateInSpecificRange(dateToCheck: Date, specificDate: Date, timeZoneId: string): boolean {
    // Start of the range: 4 AM of the specific date
    const startOfRange = moment(specificDate)
      .tz(timeZoneId)
      .startOf('day')
      .add(DayEndTime.getDayEnd(), 'hours')
      .toDate();

    // End of the range: 4 AM of the next day
    const endOfRange = moment(specificDate)
      .tz(timeZoneId)
      .startOf('day')
      .add(1, 'day')
      .add(DayEndTime.getDayEnd(), 'hours')
      .toDate();

    // Check if the dateToCheck is within the range
    return dateToCheck >= startOfRange && dateToCheck < endOfRange;
  }
  /**
   * Converts a date time to a specified format, defaulting to 2 digit hour and 2 digit minutes,
   * plus lowercase am/pm suffix
   * @param date - date object
   * @param format - optional format
   * @return formatted string time
   */
  static dateToTimeStr(date: Date | string, format?: string): string {
    const momentDate = moment(date);
    return momentDate.format(format || 'hh:mm a');
  }

  static dateToTimeStr12Hour(date: Date): string {
    return this.dateToTimeStr(date, 'h:mm A');
  }

  static minutesToTimeStr(minutesInput: number | string | undefined): string {
    if (!minutesInput) return '';
    const minutes = typeof minutesInput === 'string' ? parseInt(minutesInput, 10) : minutesInput;
    if (isNaN(minutes)) return '';

    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h${mins > 0 ? ' ' + mins + 'm' : ''}` : `${mins}m`;
  }
  // copied from bookingInterval.service.ts
  static getTimesBetweenStartAndEndTimes(openTime: moment.MomentInput, closeTime: moment.MomentInput, slotInterval: number): { time: Date }[] {

    //Format the time
    const startTime = moment(openTime);

    //Format the end time and the next day to it
    const endTime = moment(closeTime);

    //Times
    const allTimes = [];

    //Loop over the times - only pushes time with 30 minutes interval
    while (startTime < endTime) {
      //Push times
      allTimes.push({ time: new Date(startTime.format()) });
      //Add interval of 30 minutes
      startTime.add(slotInterval, 'minutes');
    }
    return allTimes;
  }
  static dateToUTCDateStr(date: Date): string {
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  static dateToVenueDateStr(date: Date, timeZoneId: string): string {
    return moment(date).tz(timeZoneId).format('YYYY-MM-DD');
  }
  static dateToDateStr(date: Date): string {
    return moment(date).format('YYYY-MM-DD');
  }

  /**
   * converts date-time object to 2050/02/01 14:30 (24 hour) format
   * @param date - date object
   * @return string time in 2050/02/01 14:30 format
   */
  static dateToDateTimeStr(date: Date) {
    const momentDate = moment(date);
    return momentDate.format('YYYY/MM/DD HH:mm');
  }

  /**
   * Converts date-time string (eg "2019-11-14T06:00:00") to a date object
   */
  static dateStrToDateTime(dateStr: string): Date {
    const momentDate = moment(dateStr);
    return momentDate.toDate();
  }

  /**
   * Converts date-time string (eg "2019-11-14T06:00:00") to a date object
   */
  static DateToDateTimeString(date: Date): string {
    const momentDate = moment(date);
    return momentDate.format('YYYY-MM-DDTHH:mm:ss');
  }

  static getViewDate(date: Date): string {
    const mom = moment(date);
    return this.getViewDateFromMoment(mom);
  }

  static getDateFromBookingId(bookingId: string): Date {
    return new Date(bookingId.split('_')[1] + 'T00:00:00.000');
  }

  /**
   * Converts date string without time info (eg "2019-11-14") to a date object
   * @param dateStr - string in format YYYY-MM-DD with optional separator character
   * @param join - optional separator character
   */
  static dateStrToDate(dateStr: string, join = '-') {
    const dateParts: string[] = dateStr.split(join);
    const year = Number(dateParts[0]);
    const month = Number(dateParts[1]) - 1;
    const day = Number(dateParts[2]);

    return new Date(year, month, day);
  }

  /**
   * Converts date string without time info (eg "2019/11/14 13:12:11") to a date object
   * @param dateStr - string in format YYYY-MM-DD with optional separator character
   * @param join - optional separator character
   */
  static dateTimeStrToDate(dateStr: string) {
    // allow the time as string transform
    const parts = dateStr.split(' ');
    const dateParts = parts[0].split('/');
    const hourParts = parts[1].split(':');

    return new Date(
      Number(dateParts[0]),
      Number(dateParts[1]) - 1,
      Number(dateParts[2]),
      Number(hourParts[0]),
      Number(hourParts[1])
    );
  }

  /**
   * Gets the current 24 hour time as a string at venue
   * @param timeZone - eg "Australia/Perth"
   * @param dateStr - optional date field, useful for testing
   */
  static getCorrectTime(timeZone: string, dateStr?: string): string {
    const browserTime: string = moment(dateStr).format();
    return moment.tz(browserTime, timeZone).format('YYYY-MM-DD H:mm:ss');
  }

  /**
   * Converts date-time string (eg "2019-11-14T06:00:00") to a date object.
   * Same as dateStrToDateTime, but doesn't rely on moment.js
   */
  static getJsDate = function (date: string): Date {
    const a: string[] = date.split(/[^0-9]/);
    const year: number = parseInt(a[0], 10);
    const month: number = parseInt(a[1], 10);
    const dayDate: number = parseInt(a[2], 10);
    const hours: number = parseInt(a[3], 10);
    const mins: number = parseInt(a[4], 10);
    const secs: number = parseInt(a[5], 10);

    return new Date(year, month - 1, dayDate, hours, mins, secs);
  };

  /**
   * Takes a moment object and timezone id and converts the moment to be relative to the venue's timezone.
   * If you were to call `.toDate()` after this you should get your local time with the offset added to it.
   * For example:
   *  - a booking in Perth at 'Thu Sep 03 2020 12:23:11'
   *  - would look like this for a Sydney user 'Thu Sep 03 2020 14:23:11 GMT+1000 (Australian Eastern Standard Time)'
   *  which has a 2 hour offset added to it
   * @param dateTime: eg a maxDate or a booking time
   * @param timeZone: eg 'Australia/Perth'
   * @param dateStr - optional date field, useful for testing
   */
  static convertMomentToVenueDateTime(dateTime: Moment, timeZone: string, dateStr?: string): void {
    dateTime.tz(timeZone);
    const userTZOffset = moment(dateStr).utcOffset();
    const venueTZOffset = dateTime.utcOffset();
    const offsetInMins = userTZOffset - venueTZOffset;

    dateTime.add(offsetInMins, 'minutes');
  }

  /**
   * returns a booking's end time as a date object, using the start time and duration
   */
  static getBookingEnd(b: { time: Date; duration: number }): Date {
    const bookingEnd = moment(b.time).add(b.duration, 'minutes');
    return bookingEnd.toDate();
  }

  static addMinutes(date: Date, mins: number): Date {
    const bookingEnd = moment(date).add(mins, 'minutes');
    return bookingEnd.toDate();
  }
  static addMinutesAndFormat(date: Date | string, mins: number, format = 'h:mmA'): string {
    const bookingEnd = moment(date).add(mins, 'minutes');
    return bookingEnd.format(format);
  }

  static subtractMinutes(date: Date, mins: number): Date {
    const bookingEnd = moment(date).subtract(mins, 'minutes');
    return bookingEnd.toDate();
  }

  static subtractSeconds(date: string | Date, seconds = 1) {
    return moment(date).subtract(seconds, 'seconds').toDate()
  }

  static getViewDateFromMoment(moment: Moment): string {
    return moment.format('dddd, MMMM D, YYYY');
  }

  static reformatDateFromDate(date: Date): Date {
    return moment(date).toDate();
  }

  /**
   * change date to another timezone, but time not change
   * @param date the date want change
   * @param timeZoneId the timezone you want go
   */
  static onlyChangeTimeZone(date: Date, timeZoneId: string): Date {
    if (!date || !timeZoneId) {
      return date;
    }
    return new Date(moment(date).parseZone().tz(timeZoneId, true).toISOString());
  }

  // difference between toVenueTime and onlyChangeTimeZone
  // Brisbane is +8, Sydney is +10 for example
  // toVenueTime: 2020-09-03T12:00:00+08:00 -> 2020-09-03T14:00:00+10:00
  // onlyChangeTimeZone: 2020-09-03T12:00:00+08:00 -> 2020-09-03T12:00:00+10:00(which is 2020-09-03T10:00:00+08:00)
  // if we pass result to same function again, toVenueTime will return 2020-09-03T16:00:00+10:00
  // onlyChangeTimeZone will return 2020-09-03T10:00:00+10:00(which is 2020-09-03T08:00:00+08:00)
  // if not familiar about time format.
  // so onlyChangeTimeZone is keep time, but change timezone. Brisbane 10:00 -> Sydney 10:00
  // if we input again, Sydney 10:00 is Brisbane 8:00, so we get Brisbane 8:00 -> Sydney 8:00
  // toVenueTime. Brisbane 10:00 -> Sydney 12:00
  // but if you input again. it won't consider timezone, so we get Sydney 12:00(but it will treat as Brisbane 12:00) -> Sydney 14:00
  // so onlyChangeTimeZone is keep decrease 2 hours, toVenueTime is keep increase 2 hours
  // if still not clear, check commented out code in test file -- describe('toVenueTime',

  // @todo: needs unit tests
  static toVenueTime(date: string | Date, timeZoneId: string): Date {
    if (typeof date === 'string') {
      date = date.split('/').join('-').split(' ').join('T');
    }
    if (!timeZoneId) {
      return new Date(date);
    }
    const momentDate = moment(date).tz(timeZoneId);
    const venueDate = new Date(momentDate.format('YYYY-MM-DD'));
    venueDate.setHours(parseInt(momentDate.format('H')));
    venueDate.setMinutes(parseInt(momentDate.format('m')));
    venueDate.setSeconds(parseInt(momentDate.format('s')));
    return venueDate;
  }

  static getVenueTime(timeZoneId: string): Date {
    const today = moment();
    // convert today's date into the venues timezone
    if (timeZoneId) {
      const year = Number(today.tz(timeZoneId).format('YYYY'));
      const month = Number(parseInt(today.tz(timeZoneId).format('M')) - 1);
      const day = Number(today.tz(timeZoneId).format('D'));
      const hours = Number(today.tz(timeZoneId).format('H'));
      const min = Number(today.tz(timeZoneId).format('m'));
      const sec = Number(today.tz(timeZoneId).format('s'));
      const venueDate = new Date(year, month, day, hours, min, sec);
      return venueDate;
    }
    return new Date();
  }
  static getClearDate(date: Date, timeZoneId: string): Date {
    if (!date) {
      date = this.getVenueTime(timeZoneId);
    }
    date = cloneDeep(date);
    date.setHours(0);
    date.setMinutes(0);
    date.setSeconds(0);
    date.setMilliseconds(0);
    return date;
  }

  static getDateFromScheduleStartTime(date: Date): Date {
    date = cloneDeep(date);
    date.setHours(DAY_END);
    date.setMinutes(0);
    date.setSeconds(0);
    date.setMilliseconds(0);
    return date;
  }

  static zeroOutMinutes(time: Date): Date {
    const selectedTimeClone = new Date(time);
    selectedTimeClone.setSeconds(0);
    selectedTimeClone.setMilliseconds(0);
    return selectedTimeClone;
  }

  static isDateToday(date: Date, timeZoneId: string, includePastMidnight = false): boolean {
    if (!date) {
      return false
    }
    const today = this.getVenueTime(timeZoneId);
    if (includePastMidnight && this.isTimeSmallerThanDayEnd(date)) {
      date = cloneDeep(date); // since we're mutating the date, we'd better make a copy
      date.setDate(date.getDate() - 1);
    }
    return this.isOnSameDate(date, today);
  }

  /**
   * Convert time string to a Date
   */
  static timeToDate(timeStr: string | Date, fullDate: Date): Date {
    if (timeStr instanceof Date) {
      return timeStr;
    }
    if (!fullDate) {
      console.error('timeToDate fullDate input undefined, timeStr', timeStr);
      return null;
    }
    if (!timeStr) {
      console.error('timeToDate timeStr input undefined, fullDate', fullDate);
      return null;
    }
    if (timeStr.indexOf(':') === -1) {
      console.error('timeToDate timeStr input format', timeStr, fullDate);
      return null;
    }
    const timeParts = timeStr.split(':');
    let hour = Number(timeParts[0]);
    const min = Number(timeParts[1]);
    const date = new Date(fullDate.getTime());
    hour = hour === 24 ? 0 : hour;
    date.setHours(hour);
    date.setMinutes(min);
    date.setSeconds(0);
    date.setMilliseconds(0);
    // if the time is before 4am amke it the next day
    // our day runs from 4am - 4am
    //TODO add this to config
    if (this.isTimeSmallerThanDayEnd(date)) {
      date.setDate(date.getDate() + 1);
    }
    return date;
  }

  static checkDateDay(date: Date, orgDate: Date): Date {
    if (date && orgDate) {
      // make sure the date is the original date
      this.copyDateOnly(date, orgDate);
      // if the time is before 4am make it the next day
      // our day runs from 4am - 4am
      //TODO add this to config
      if (this.isTimeSmallerThanDayEnd(date)) {
        date.setDate(orgDate.getDate() + 1);
      } else {
        date.setDate(orgDate.getDate());
      }
    }
    return date;
  }

  static copyDateOnly(date: Date, orgDate: Date): void {
    date.setDate(orgDate.getDate());
    date.setMonth(orgDate.getMonth());
    date.setFullYear(orgDate.getFullYear());
  }

  static closeTimesToDate(timeStr: string, openDate: Date): Date {
    const closeDate = this.timeToDate(timeStr, openDate);
    if (closeDate && closeDate.getTime() < openDate.getTime()) {
      // the close time is before the open time make it occour on the next day
      closeDate.setDate(closeDate.getDate() + 1);
    } else if (closeDate && closeDate.getDate() !== openDate.getDate()) {
      closeDate.setDate(openDate.getDate());
      closeDate.setMonth(openDate.getMonth());
    }
    return closeDate;
  }

  static isOnOrInBetweenDates(date: Date, startDate: Date, endDate: Date): boolean {
    return date <= endDate && date >= startDate;
  }

  static daysBetweenDates(startDate: Date, endDate: Date): number {
    return Math.abs(startDate?.valueOf() - endDate?.valueOf()) / (1000 * 60 * 60 * 24);
  }

  static hoursBetweenDates(startDate: Date, endDate: Date): number {
    let diff = 0;
    if (startDate && endDate) {
      const startOffset = startDate.getTimezoneOffset();
      const endOffset = endDate.getTimezoneOffset();
      diff = startOffset - endOffset;
    }
    let hours = Math.abs(startDate?.valueOf() - endDate?.valueOf()) / (60 * 60 * 1000);
    if (diff !== 0) {
      diff = diff / 60;
      hours = hours + diff;
    }
    return hours;
  }

  static minsBetweenDates(startDate: Date, endDate: Date): number {
    let diff = 0;
    let mins = 0;
    if (startDate && endDate) {
      const startOffset = startDate.getTimezoneOffset();
      const endOffset = endDate.getTimezoneOffset();
      diff = startOffset - endOffset;
    }
    mins = Math.abs(startDate?.valueOf() - endDate?.valueOf()) / (60 * 1000);
    if (diff !== 0) {
      mins = mins + diff;
    }
    return mins;
  }

  static addMinutesToTime(date: Date, minutes: number): Date {
    return new Date(date.setMinutes(date.getMinutes() + minutes));
  }

  static getMinOfDay(date: Date, time: Date | string): number {
    if (!(time instanceof Date)) {
      time = this.dateTimeStrToDate(time);
    }
    return this.minsBetweenDates(date, time);
  }

  static isOnSameDate(date1: Date, date2: Date, include4AMCheck = false): boolean {
    if (!(date1 instanceof Date) || !(date2 instanceof Date)) {
      console.error('isOnSameDate invalid inputs', date1, date2);
      return false;
    }

    let date1Day = date1.getDate();
    let date2Day = date2.getDate();
    if (include4AMCheck) {
      if (this.isTimeSmallerThanDayEnd(date1)) {
        date1Day = date1.getDate() - 1;
      }
      if (this.isTimeSmallerThanDayEnd(date2)) {
        date2Day = date2.getDate() - 1;
      }
    }

    return (
      date1Day === date2Day &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear()
    );
  }

  /**
   * Returns a new moment date from the start of the day, using the date provided
   * @param dateTime {Date} - any date time
   * @param returnAsDate {Boolean} - if true, returns as Date, otherwise Moment
   * @returns {Moment} - moment from the beginning of the provided day
   */
  static getStartOfDay(dateTime: Date, returnAsDate = false): Date | moment.Moment {
    const m = moment(dateTime).startOf('day');
    return returnAsDate ? m.toDate() : m;
  }

  /**
   *
   * @param standByConfirmationExpiry {string} - date that standby request expires
   * @returns {boolean}
   */
  static checkIfStandbyConfirmationExpired(standByConfirmationExpiry: string): boolean {
    if (!standByConfirmationExpiry) {
      return false;
    }

    const defaultDate = '0001/01/01 00:00';
    if (standByConfirmationExpiry !== defaultDate) {
      const extendedTime = moment.utc(standByConfirmationExpiry).local();
      const currentTime = moment();
      const timeDifference = extendedTime.diff(currentTime, 'minutes');
      return timeDifference < 0;
    }

    return false;
  }

  static setTimeFromString(date: Date, time: string): Date {
    // get the hours and minutes as numbers
    const h = parseInt(time.split(':')[0], 10);
    const m = parseInt(time.split(':')[1], 10);
    // then adds it to existing date object
    const existingDate = moment(date);
    existingDate.set({ h, m });

    return existingDate.toDate();
  }

  static setFutureDateInISO(date: Date, years: number): string {
    const futureDate = cloneDeep(date);
    futureDate.setFullYear(date.getFullYear() + years);
    return futureDate.toISOString().split('.')[0];
  }

  static dateToIdStr(date: Date | number): string {
    // date could be Date or number
    return moment(date).format('YYYY-MM-DD');
  }

  // widget
  static getCurrentTimeByTimeZone(timeZoneId: string, returnAsMoment = false): Date | Moment {
    const mom: Moment = moment.tz(moment(), timeZoneId);

    if (returnAsMoment) return mom;

    return mom.toDate();
  }

  /**
   * Adds the UTC offset to a Date object to get the time as local
   */
  // widget
  static getDateWithOffset(date?: Date, returnAsMoment = false): Date | Moment {
    const newMoment = date ? moment(date) : moment();
    newMoment.add(newMoment.utcOffset(), 'minutes');

    if (returnAsMoment) return newMoment;
    return newMoment.toDate();
  }

  // widget
  static checkForLastDate(date: Moment): boolean {
    const tomorrow = moment().add(1, 'days');
    return tomorrow.isSameOrAfter(date, 'day');
  }

  // note: this doesn't take into account after midnight in Diary
  static isDateSame(date1: Date, date2: Date, minuteAccuracy = false): boolean {
    if (minuteAccuracy) {
      return moment(date1).isSame(date2, 'minutes');
    }

    return moment(date1).isSame(date2);
  }

  static isBeforeDate(date1: Date, date2: Date): boolean {
    return moment(date1).isBefore(date2);
  }

  static isAfterDate(date1: Date, date2: Date): boolean {
    return moment(date1).isAfter(date2);
  }
  static isBeforeToday4AM(dateToCheck: Date, timeZoneId: string) {
    const today = this.getVenueTime(timeZoneId);
    today.setHours(DAY_END, 0, 0, 0); // Set today's date to 4 AM

    return dateToCheck < today; // Returns true if dateToCheck is before today at 4 AM
  }
  static isBeforeToday(dateToCheck: Date, timeZoneId: string) {
    const today = this.getVenueTime(timeZoneId);
    today.setHours(0, 0, 0, 0); // Set today's date to 0 AM
    return dateToCheck < today; // Returns true if dateToCheck is before today at 0 AM
  }
  // check if two periods overlap, like check whether booking in 90 mins, or in 8 hours.
  static doPeriodsInteract(
    start1: number, end1: number, start2: number, end2: number
  ): boolean {
    return start1 <= end2 && start2 <= end1;
  }
  static getDifferenceInMS(date1: Date, date2: Date): number {
    const result = Math.abs(date1.getTime() - date2.getTime());
    return result;
  }

  static getSanitizedDate(dateStr: Date | string): Date {
    if (dateStr instanceof Date) {
      return dateStr;
    }

    return new Date(dateStr);
  }

  static getDateWithDayOffset(date: Date, days: number, useMinus = false): Date {
    if (!days) {
      return date;
    }
    const mom = moment(date);
    const newMom = useMinus ? mom.subtract(days, 'days') : mom.add(days, 'days');
    return newMom.toDate();
  }


  /* This function checks if the booking date is in the past - It also considers the case where the booking is on the previous day
     but after 12 am and before 4 am - that is still a past booking
  */
  static checkIfBookingInPastDay(bookingDateTime: Date, currentVenueDateTime: Date): boolean {

    // Using toLocaleDateString to remove the time part and only get the date - converting to moment so that useBefore/isSame function could be used
    const bookingDate = moment(bookingDateTime).startOf('day')
    const currentVenueDate = moment(currentVenueDateTime).startOf('day')

    // checking if the date is same and time before 4am then the booking is previous day booking
    const isPreviousDayBookingAfter12 = bookingDate.isSame(currentVenueDate) && this.isTimeSmallerThanDayEnd(bookingDateTime);
    return bookingDate.isBefore(currentVenueDate) || isPreviousDayBookingAfter12; // if the date is previous day or day is same day but before 4am
  }

  static normaliseDateWhenVenueTimeIsPastMidnight(date: Date, timeZoneId: string): Date {
    const venueTime = this.getVenueTime(timeZoneId);
    if (this.isTimeSmallerThanDayEnd(venueTime) && this.isOnSameDate(date, venueTime) && this.isTimeGreaterOrEqualsDayEnd(date)) {
      date = cloneDeep(date); // since we're mutating the date, we'd better make a copy
      date.setDate(date.getDate() - 1);
    }
    return date;
  }

  static isValidDate(date: Date): boolean {
    return date instanceof Date && !isNaN(new Date(date).valueOf())
  }

  /**
   * @todo Need to check function name or logic for remaining
   */
  static getRemainingMins(startTime: Date, endTime: Date, timeZoneId: string): number {
    const venueTime = this.getVenueTime(timeZoneId);

    if (startTime >= endTime) {
      return 0;
    }

    const remainingMS = endTime.getTime() - venueTime.getTime();
    if (remainingMS > 0) {
      return Math.ceil(remainingMS / 1000 / 60);
    }

    return 0;
  }

  static getPassedMins(startTime: Date, selectedTime: Date): number {

    if (selectedTime <= startTime) {
      return 0;
    }

    const remainingMS = selectedTime.getTime() - startTime.getTime();
    if (remainingMS > 0) {
      return Math.ceil(remainingMS / 1000 / 60);
    }

    return 0;
  }
  static getOldestTime(date1: Date, date2: Date): Date {
    return date1 > date2 ? date1 : date2;
  }

  static getClosestDate(list: { value: Date }[], compareTo: Date): Date {
    if (!list.length) {
      return compareTo;
    }
    const closestTimeInMS = compareTo.getTime();
    const listInMS = list.map(l => l.value.getTime());
    const newArr = [];
    for (let i = 0; i < listInMS.length; i++) {
      newArr.push(Math.abs(closestTimeInMS - listInMS[i]));
    }
    // eslint-disable-next-line prefer-spread
    const closestIndex = newArr.indexOf(Math.min.apply(Math, newArr));
    return list[closestIndex].value;
  }
  static areDatesSameExceptSeconds(date1: Date, date2: Date): boolean {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate() &&
      date1.getHours() === date2.getHours() &&
      date1.getMinutes() === date2.getMinutes()
    );
  }

  static getDateInFuture(amount = 1, unit: DurationInputArg2 = 'days', asString = true): string | Date {
    const mom = moment().add(amount, unit);
    if (asString) {
      return mom.toString();
    }
    return mom.toDate();
  }

  static isAfterNow(expirationDate: string | Date): boolean {
    return moment().isAfter(moment(expirationDate));
  }

  // this will return true if the selected time between the booking times (30 mins before booking start time - till the endtime but doesnt include the endtime)
  static isSelectedTimeBetween2TimesIgnoreEndTime(selectedTime: Date, startTime: Date, endTime: Date): boolean {
    const bookingStartingTime = moment(startTime).subtract(BOOKING_START_TIME_OFFSET, 'minutes').toDate();
    return moment(selectedTime).isBetween(bookingStartingTime, endTime, null, '[)')
  }

  // this will return true if the time is between the 2 times
  static isTimeBetween2Times(selectedTime: Date, startTime: Date, endTime: Date, inclusivity: '()' | '[)' | '(]' | '[]' = '[)'): boolean {
    return moment(selectedTime).isBetween(startTime, endTime, undefined, inclusivity)
  }

  /**
   * Checks if the time of the first date is between the times of the second and third dates
   * Only compares time values, ignoring the date part
   * @param timeToCheck - the date whose time we want to check
   * @param startTime - the start time boundary
   * @param endTime - the end time boundary
   * @return true if the time is between the two times, false otherwise
   */
  static isTimeBetweenTwoTimes(timeToCheck: Date, startTime: Date, endTime: Date): boolean {
    if (!timeToCheck || !startTime || !endTime) {
      return false;
    }
    // Extract only the time components (hours, minutes, seconds, milliseconds)
    const timeToCheckMS = timeToCheck.getHours() * 3600000 +
      timeToCheck.getMinutes() * 60000 +
      timeToCheck.getSeconds() * 1000 +
      timeToCheck.getMilliseconds();

    const startTimeMS = startTime.getHours() * 3600000 +
      startTime.getMinutes() * 60000 +
      startTime.getSeconds() * 1000 +
      startTime.getMilliseconds();

    const endTimeMS = endTime.getHours() * 3600000 +
      endTime.getMinutes() * 60000 +
      endTime.getSeconds() * 1000 +
      endTime.getMilliseconds();

    // Check if timeToCheck is between startTime and endTime
    // Handle case where startTime is after endTime (spans midnight)
    if (startTimeMS <= endTimeMS) {
      return timeToCheckMS >= startTimeMS && timeToCheckMS <= endTimeMS;
    } else {
      // Time range spans midnight (e.g., 22:00 to 06:00)
      return timeToCheckMS >= startTimeMS || timeToCheckMS <= endTimeMS;
    }
  }

  static getHoursMinsSecondsFromMS(ms: number): { hours: number, mins: number, seconds: number } {
    const hours = Math.floor(ms / 3600000);
    const mins = Math.floor((ms % 3600000) / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return { hours, mins, seconds };
  }
}
